import { useDispatch, useSelector } from 'react-redux';
import React, { createContext, useCallback, useContext, useEffect, useRef, useMemo } from 'react';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import { channelsParser, Collection } from 'utils/fnParser';
import { Channel } from 'types/Asset';
import GSApi, { getLastChannelsRequestCacheKey } from 'api/Api';
import { RootState } from 'providers/reducers';
import { channelsAction, ChannelsStore } from 'providers/reducers/channel-reducer';
import { useGlobalNetworkError, useGlobalNoInternet } from 'hooks/withNetworkCheck/withNetworkCheck';
import { useAuth } from 'providers/useAuth/AuthContext';
import { MINUTES } from 'utils/TimeUnit';

const REFRESH_CHANNELS_PERIOD = MINUTES.toMillis(30);

// Module-level flag to prevent race conditions
let isFetchingChannels = false;
let currentFetch: Promise<any> | null = null;
let fetchGeneration = 0;

const useChannelsService = () => {
    const { onNoInternet, noInternet } = useGlobalNoInternet();
    const { onNetworkError, networkError } = useGlobalNetworkError();
    const { loading, fetcher } = useDataFetcher<Collection<Channel>, { skipCache?: boolean }>(
        (params) => GSApi.fetchChannels<Collection<Channel>>(channelsParser, params?.skipCache),
        onNoInternet,
        onNetworkError
    );

    const favorites = useDataFetcher<any, any>(() => GSApi.fetchFavorites<any>((res) => res), onNoInternet, onNetworkError);
    const channelData = useSelector<RootState, ChannelsStore>((state) => state.channelsStore);
    const dispatch = useDispatch();
    const { cacheKey } = useAuth();

    // Memoize channelData to prevent unnecessary recreations of fetchChannels
    const channelState = useMemo(
        () => channelData,
        [channelData?.channels, channelData?.loading, channelData?.error, channelData?.isFetching]
    );
    const inLoading = useRef(loading);
    const reloadChannelsScheduleRef = useRef<number>(null);

    const channelsFetcherRef = useRef(fetcher);
    const favoritesFetcherRef = useRef(favorites.fetcher);

    useEffect(() => {
        channelsFetcherRef.current = fetcher;
        favoritesFetcherRef.current = favorites.fetcher;
    }, [fetcher, favorites.fetcher]);

    const scheduleReloadChannels = () => {
        clearTimeout(reloadChannelsScheduleRef.current);
        reloadChannelsScheduleRef.current = window.setTimeout(() => {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            fetchChannels(true);
        }, REFRESH_CHANNELS_PERIOD);
    };

    const runFetch = useCallback(
        async (force: boolean) => {
            fetchGeneration += 1;
            const generation = fetchGeneration;

            isFetchingChannels = true;
            dispatch(channelsAction('CHANNELS_FETCHING_START', null));
            dispatch(channelsAction('CHANNELS_LOADING', null));

            try {
                const [channelsResult, favoritesResult] = await Promise.all([
                    channelsFetcherRef.current(force ? { skipCache: true } : null),
                    favoritesFetcherRef.current(null),
                ]);

                if (generation !== fetchGeneration) {
                    return { superseded: true };
                }

                const items: Channel[] | null = channelsResult?.response?.items ?? null;

                if (!items) {
                    dispatch(channelsAction('CHANNELS_LOAD_ERROR', null));
                    scheduleReloadChannels();
                    return { channels: null, error: channelsResult?.error ?? true, loading: false };
                }

                const favoriteChannelIds = new Set<string>(favoritesResult?.response?.channel_ids ?? []);
                const parsedChannels = items.map((channel) => ({ ...channel, favorite: favoriteChannelIds.has(channel.id) }));

                dispatch(channelsAction('STORE_CHANNELS', parsedChannels));
                scheduleReloadChannels();

                return { channels: parsedChannels, error: null, loading: false };
            } finally {
                if (generation === fetchGeneration) {
                    isFetchingChannels = false;
                }
            }
        },
        [dispatch]
    );

    const setCurrentFetch = (promise: Promise<any>) => {
        const started: Promise<any> = promise.finally(() => {
            if (currentFetch === started) {
                currentFetch = null;
            }
        });
        currentFetch = started;
        return started;
    };

    const fetchChannels = useCallback(
        async (force: boolean = false) => {
            if (force) {
                return setCurrentFetch(runFetch(true));
            }

            if (currentFetch) {
                return currentFetch;
            }

            if (isFetchingChannels || channelState?.isFetching) {
                return undefined;
            }

            const shouldFetch =
                (!channelState || (!channelState.loading && !channelState.error && !channelState.channels)) && !noInternet && !networkError;

            // no data in cache
            if (shouldFetch) {
                return setCurrentFetch(runFetch(false));
            }

            return channelState;
        },
        [channelState, noInternet, networkError, runFetch]
    );

    useEffect(() => {
        inLoading.current = loading;
    }, [loading]);

    useEffect(
        () => () => {
            if (inLoading.current) {
                dispatch(channelsAction('CHANNELS_INTERRUPTED', null));
            }
        },
        []
    );

    useEffect(() => {
        if (!cacheKey || !channelState?.channels) {
            return;
        }

        if (cacheKey === getLastChannelsRequestCacheKey()) {
            return;
        }

        dispatch(channelsAction('CLEAR_CHANNELS', null));
        fetchChannels(true);
    }, [cacheKey, channelState?.channels, dispatch, fetchChannels]);

    const getChannelById = (channelId: string) => channelData?.channels?.find((channel) => channel.id === channelId);

    return {
        channels: channelState?.channels,
        loading: channelState?.loading ?? loading,
        isFetching: channelState?.isFetching ?? false,
        fetchChannels,
        getChannelById,
    };
};

const ChannelsContext = createContext<ReturnType<typeof useChannelsService>>(null);

export const ChannelsProvider = ({ children }) => {
    const channels = useChannelsService();
    return React.createElement(ChannelsContext.Provider, { value: channels }, children);
};

export const useChannels = () => useContext(ChannelsContext);
